(function () {
  var existing = window.BCDeliveryEstimator;

  if (existing && existing.version === '1.0.0') {
    existing.initAll();
    return;
  }

  var fallbackZones = {
    tier_1_2_prefixes: [],
    remote_prefixes: [],
    holidays: [],
    messages: {
      invalid: 'Please enter a valid 6-digit pincode.',
      not_serviceable: 'Pincode not serviceable.',
      info_note: 'Delivery date is an estimate and may vary based on courier serviceability, holidays, and operational conditions.'
    }
  };

  var zonesRequests = {};
  var rootSelector = '[data-bc-delivery-estimator]';
  var delegatedListenersBound = false;

  function toArray(value) {
    return Array.isArray(value) ? value.map(String) : [];
  }

  function normalizeZones(zones) {
    var source = zones && typeof zones === 'object' ? zones : {};

    return {
      tier_1_2_prefixes: toArray(source.tier_1_2_prefixes),
      remote_prefixes: toArray(source.remote_prefixes),
      not_serviceable_prefixes: toArray(source.not_serviceable_prefixes),
      not_serviceable_pincodes: toArray(source.not_serviceable_pincodes),
      holidays: toArray(source.holidays),
      messages: Object.assign({}, fallbackZones.messages, source.messages || {})
    };
  }

  function fetchZones(url) {
    if (!url) {
      return Promise.resolve(normalizeZones(fallbackZones));
    }

    if (!zonesRequests[url]) {
      zonesRequests[url] = fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Delivery zones unavailable');
          }

          return response.json();
        })
        .then(normalizeZones)
        .catch(function () {
          return normalizeZones(fallbackZones);
        });
    }

    return zonesRequests[url];
  }

  function isValidIndianPincode(pincode) {
    return /^[1-9]\d{5}$/.test(String(pincode || '').trim());
  }

  function getPincodePrefix(pincode) {
    return String(pincode || '').trim().slice(0, 3);
  }

  function getDateKey(date) {
    var year = date.getFullYear();
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var day = String(date.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  function isHoliday(date, holidays) {
    return holidays.indexOf(getDateKey(date)) !== -1;
  }

  function isSunday(date) {
    return date.getDay() === 0;
  }

  function addWorkingDays(startDate, workingDays, holidays) {
    var result = new Date(startDate);
    var daysAdded = 0;
    var guard = 0;

    result.setHours(12, 0, 0, 0);

    while (daysAdded < workingDays && guard < 400) {
      result.setDate(result.getDate() + 1);

      if (!isSunday(result) && !isHoliday(result, holidays)) {
        daysAdded += 1;
      }

      guard += 1;
    }

    return result;
  }

  function formatDeliveryDate(date, today) {
    var referenceDate = today || new Date();
    var options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    };

    if (date.getFullYear() !== referenceDate.getFullYear()) {
      options.year = 'numeric';
    }

    return new Intl.DateTimeFormat('en-IN', options).format(date);
  }

  function getNumber(value, fallback) {
    var number = parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  }

  function getSettings(root, zones) {
    var tierDays = getNumber(root.dataset.tierDays, 3);
    var otherMinDays = getNumber(root.dataset.otherMinDays, 4);
    var otherMaxDays = getNumber(root.dataset.otherMaxDays, 7);
    var remoteDays = getNumber(root.dataset.remoteDays, 7);
    var extraDays = Math.max(0, getNumber(root.dataset.extraDays, 0));

    if (otherMaxDays < otherMinDays) {
      otherMaxDays = otherMinDays;
    }

    return {
      tierDays: tierDays + extraDays,
      otherMinDays: otherMinDays + extraDays,
      otherMaxDays: otherMaxDays + extraDays,
      remoteDays: remoteDays + extraDays,
      invalidMessage: root.dataset.invalidMessage || zones.messages.invalid || fallbackZones.messages.invalid,
      notServiceableMessage: root.dataset.notServiceableMessage || zones.messages.not_serviceable || fallbackZones.messages.not_serviceable,
      infoNote: root.dataset.infoNote || zones.messages.info_note || fallbackZones.messages.info_note,
      productMessage: root.dataset.productMessage || '',
      showIcon: root.dataset.showIcon !== 'false',
      enableAnimations: root.dataset.enableAnimations !== 'false'
    };
  }

  function calculateDeliveryEstimate(pincode, zones, settings, startDate) {
    var cleanPincode = String(pincode || '').trim();
    var today = startDate || new Date();
    var prefix = getPincodePrefix(cleanPincode);
    var minDate;
    var maxDate;
    var deliveryDate;

    if (!isValidIndianPincode(cleanPincode)) {
      return {
        status: 'error',
        code: 'invalid',
        message: settings.invalidMessage
      };
    }

    if (
      zones.not_serviceable_pincodes.indexOf(cleanPincode) !== -1 ||
      zones.not_serviceable_prefixes.indexOf(prefix) !== -1
    ) {
      return {
        status: 'error',
        code: 'not_serviceable',
        message: settings.notServiceableMessage
      };
    }

    if (zones.tier_1_2_prefixes.indexOf(prefix) !== -1) {
      deliveryDate = addWorkingDays(today, settings.tierDays, zones.holidays);

      return {
        status: 'success',
        code: 'tier_1_2',
        message: 'Estimated Delivery by ' + formatDeliveryDate(deliveryDate, today),
        minDate: deliveryDate,
        maxDate: deliveryDate
      };
    }

    if (zones.remote_prefixes.indexOf(prefix) !== -1) {
      deliveryDate = addWorkingDays(today, settings.remoteDays, zones.holidays);

      return {
        status: 'success',
        code: 'remote',
        message: 'Estimated Delivery by ' + formatDeliveryDate(deliveryDate, today),
        minDate: deliveryDate,
        maxDate: deliveryDate
      };
    }

    minDate = addWorkingDays(today, settings.otherMinDays, zones.holidays);
    maxDate = addWorkingDays(today, settings.otherMaxDays, zones.holidays);

    return {
      status: 'success',
      code: 'other',
      message: 'Estimated Delivery between ' + formatDeliveryDate(minDate, today) + ' - ' + formatDeliveryDate(maxDate, today),
      minDate: minDate,
      maxDate: maxDate
    };
  }

  function createResultIcon(status) {
    var icon = document.createElement('span');
    var iconText = document.createElement('span');

    icon.className = 'bc-delivery-estimator__result-icon';
    icon.setAttribute('aria-hidden', 'true');
    iconText.textContent = status === 'success' ? '✓' : '!';
    icon.appendChild(iconText);

    return icon;
  }

  function createInfoNote(settings, root) {
    var note = document.createElement('div');
    var noteId = 'bc-delivery-note-' + (root.dataset.sectionId || 'section') + '-' + (root.dataset.blockId || 'block');

    note.id = noteId;
    note.className = 'bc-delivery-estimator__info-note';
    note.setAttribute('aria-hidden', 'true');
    note.textContent = settings.infoNote;

    return note;
  }

  function renderResult(root, estimate, settings) {
    var result = root.querySelector('.bc-delivery-estimator__result');
    var input = root.querySelector('.bc-delivery-estimator__input');
    var row = document.createElement('div');
    var text = document.createElement('span');
    var note;

    if (!result) {
      return;
    }

    result.classList.remove('is-visible');
    result.textContent = '';
    row.className = 'bc-delivery-estimator__result-row is-' + estimate.status;

    if (settings.showIcon) {
      row.appendChild(createResultIcon(estimate.status));
    }

    text.className = 'bc-delivery-estimator__result-text';
    text.textContent = estimate.message;
    row.appendChild(text);

    if (estimate.status === 'success') {
      var infoButton = document.createElement('button');
      note = createInfoNote(settings, root);

      infoButton.type = 'button';
      infoButton.className = 'bc-delivery-estimator__info-button';
      infoButton.setAttribute('aria-label', 'More delivery estimate information');
      infoButton.setAttribute('aria-expanded', 'false');
      infoButton.setAttribute('aria-controls', note.id);
      infoButton.textContent = 'i';

      row.appendChild(infoButton);
    }

    result.appendChild(row);

    if (note) {
      result.appendChild(note);
    }

    if (estimate.status === 'success' && settings.productMessage) {
      var productMessage = document.createElement('div');
      productMessage.className = 'bc-delivery-estimator__product-message is-visible';
      productMessage.textContent = settings.productMessage;
      result.appendChild(productMessage);
    }

    if (input) {
      if (estimate.status === 'error') {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }

    if (settings.enableAnimations) {
      window.requestAnimationFrame(function () {
        result.classList.add('is-visible');
      });
    } else {
      root.classList.add('bc-delivery-estimator--no-animations');
      result.classList.add('is-visible');
    }
  }

  function setLoading(root, isLoading) {
    var button = root.querySelector('.bc-delivery-estimator__button');

    root.setAttribute('aria-busy', String(isLoading));

    if (button) {
      button.disabled = isLoading;
    }
  }

  function submitEstimate(root) {
    var input = root.querySelector('.bc-delivery-estimator__input');

    if (!input) {
      return;
    }

    setLoading(root, true);

    fetchZones(root.dataset.zonesUrl)
      .then(function (zones) {
        var settings = getSettings(root, zones);
        var estimate = calculateDeliveryEstimate(input.value, zones, settings);
        renderResult(root, estimate, settings);
      })
      .finally(function () {
        setLoading(root, false);
      });
  }

  function bindDelegatedListeners() {
    if (delegatedListenersBound) {
      return;
    }

    delegatedListenersBound = true;

    document.addEventListener('input', function (event) {
      if (event.target && event.target.matches('.bc-delivery-estimator__input')) {
        event.target.removeAttribute('aria-invalid');
      }
    });

    document.addEventListener('submit', function (event) {
      var form = event.target && event.target.closest('.bc-delivery-estimator__form');
      var root = form && form.closest(rootSelector);

      if (!root) {
        return;
      }

      event.preventDefault();
      submitEstimate(root);
    });

    document.addEventListener('click', function (event) {
      var infoButton = event.target && event.target.closest('.bc-delivery-estimator__info-button');
      var root = infoButton && infoButton.closest(rootSelector);
      var noteId = infoButton && infoButton.getAttribute('aria-controls');
      var note = noteId ? document.getElementById(noteId) : null;

      if (!root || !note) {
        return;
      }

      event.preventDefault();
      var isOpen = note.classList.toggle('is-open');
      note.setAttribute('aria-hidden', String(!isOpen));
      infoButton.setAttribute('aria-expanded', String(isOpen));
    });
  }

  function init(root) {
    if (!root || root.dataset.bcDeliveryEstimatorInitialized === 'true') {
      return;
    }

    root.dataset.bcDeliveryEstimatorInitialized = 'true';
  }

  function initAll(scope) {
    var context = scope || document;
    var roots = context.matches && context.matches(rootSelector)
      ? [context]
      : Array.prototype.slice.call(context.querySelectorAll(rootSelector));

    bindDelegatedListeners();
    roots.forEach(init);
  }

  window.BCDeliveryEstimator = {
    version: '1.0.0',
    init: init,
    initAll: initAll,
    isValidIndianPincode: isValidIndianPincode,
    getPincodePrefix: getPincodePrefix,
    isHoliday: isHoliday,
    isSunday: isSunday,
    addWorkingDays: addWorkingDays,
    formatDeliveryDate: formatDeliveryDate,
    calculateDeliveryEstimate: calculateDeliveryEstimate
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
